import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FileText, 
  Shield, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Download,
  Eye,
  Lock,
  Mail,
  ExternalLink
} from 'lucide-react';
import WorkflowTabView from '../../../components/WorkflowTabView';

const AdvancedComplianceWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  
  // Load data from localStorage on component mount
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem('advancedComplianceWorkflowData');
      const savedStep = localStorage.getItem('advancedComplianceWorkflowStep');
      
      if (savedData) {
        return {
          formData: JSON.parse(savedData),
          currentStep: savedStep ? parseInt(savedStep) : 1
        };
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    
    return {
      formData: {
        xbrlTagging: {},
        digitalSignature: {},
        earlyAccess: {}
      },
      currentStep: 1
    };
  };

  const { formData: initialFormData, currentStep: initialStep } = loadFromStorage();
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData] = useState(initialFormData);
  const [earlyAccessData, setEarlyAccessData] = useState({
    useCase: '',
    regulatorMarket: '',
    deadline: '',
    contactEmail: ''
  });

  // Define workflow steps
  const steps = [
    {
      id: 1,
      title: t('workflow.advancedCompliance.steps.xbrlTagging.title'),
      description: t('workflow.advancedCompliance.steps.xbrlTagging.description'),
      icon: FileText
    },
    {
      id: 2,
      title: t('workflow.advancedCompliance.steps.digitalSignature.title'),
      description: t('workflow.advancedCompliance.steps.digitalSignature.description'),
      icon: Shield
    },
    {
      id: 3,
      title: t('workflow.advancedCompliance.steps.earlyAccess.title'),
      description: t('workflow.advancedCompliance.steps.earlyAccess.description'),
      icon: Clock
    }
  ];

  // Save data to localStorage
  const saveToStorage = (newFormData, newStep) => {
    try {
      localStorage.setItem('advancedComplianceWorkflowData', JSON.stringify(newFormData));
      localStorage.setItem('advancedComplianceWorkflowStep', newStep.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handleStepClick = (stepId) => {
    setCurrentStep(stepId);
    saveToStorage(formData, stepId);
  };



  const handleEarlyAccessInputChange = (field, value) => {
    setEarlyAccessData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEarlyAccessSubmit = (e) => {
    e.preventDefault();
    // Handle early access request submission
    console.log('Early access request submitted:', earlyAccessData);
    // Show success message or redirect
  };

  const renderXBRLTaggingSection = () => (
    <div className="space-y-6">
      {/* Header Chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Feature flags: XBRL OFF • Signature OFF
        </span>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Status: Not available yet
        </span>
      </div>

      {/* Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-blue-800 font-medium">{t('workflow.advancedCompliance.banner.title')}</p>
            <p className="text-blue-700 text-sm mt-1">{t('workflow.advancedCompliance.banner.description')}</p>
          </div>
        </div>
      </div>

      {/* Read-only fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.advancedCompliance.xbrl.taxonomy')}
          </label>
          <p className="text-gray-900">EFRAG ESRS (latest)</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.advancedCompliance.xbrl.entity')}
          </label>
          <p className="text-gray-900">Legal name / LEI</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.advancedCompliance.xbrl.period')}
          </label>
          <p className="text-gray-900">2024</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.advancedCompliance.xbrl.currency')}
          </label>
          <p className="text-gray-900">EUR</p>
        </div>
      </div>

      {/* Mapping preview table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">
            {t('workflow.advancedCompliance.xbrl.mappingPreview')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('workflow.advancedCompliance.xbrl.concept')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('workflow.advancedCompliance.xbrl.sourceToken')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('workflow.advancedCompliance.xbrl.context')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('workflow.advancedCompliance.xbrl.unit')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('workflow.advancedCompliance.xbrl.precision')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('workflow.advancedCompliance.xbrl.status')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm text-gray-900">E1-1</td>
                <td className="px-4 py-3 text-sm text-gray-900">scope1_total</td>
                <td className="px-4 py-3 text-sm text-gray-900">2024</td>
                <td className="px-4 py-3 text-sm text-gray-900">tCO2e</td>
                <td className="px-4 py-3 text-sm text-gray-900">2</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {t('workflow.advancedCompliance.xbrl.mapped')}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-900">E1-2</td>
                <td className="px-4 py-3 text-sm text-gray-900">scope2_total</td>
                <td className="px-4 py-3 text-sm text-gray-900">2024</td>
                <td className="px-4 py-3 text-sm text-gray-900">tCO2e</td>
                <td className="px-4 py-3 text-sm text-gray-900">2</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {t('workflow.advancedCompliance.xbrl.pending')}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Validation preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              {t('workflow.advancedCompliance.xbrl.schema')}
            </h4>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">0</p>
          <p className="text-sm text-gray-500">errors</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              {t('workflow.advancedCompliance.xbrl.calculation')}
            </h4>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">0</p>
          <p className="text-sm text-gray-500">errors</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              {t('workflow.advancedCompliance.xbrl.consistency')}
            </h4>
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-yellow-600">2</p>
          <p className="text-sm text-gray-500">warnings</p>
        </div>
      </div>

      {/* Disabled buttons */}
      <div className="flex flex-wrap gap-3">
        <button 
          disabled 
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
        >
          <FileText className="h-4 w-4 mr-2" />
          {t('workflow.advancedCompliance.xbrl.generateXBRL')}
        </button>
        <button 
          disabled 
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          {t('workflow.advancedCompliance.xbrl.validate')}
        </button>
        <button 
          disabled 
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
        >
          <Download className="h-4 w-4 mr-2" />
          {t('workflow.advancedCompliance.xbrl.downloadPackage')}
        </button>
      </div>
    </div>
  );

  const renderDigitalSignatureSection = () => (
    <div className="space-y-6">
      {/* Read-only fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.advancedCompliance.signature.certificateSource')}
          </label>
          <p className="text-gray-900">HSM/PFX</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.advancedCompliance.signature.hash')}
          </label>
          <p className="text-gray-900">SHA-256</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.advancedCompliance.signature.tsaEndpoint')}
          </label>
          <p className="text-gray-900">RFC 3161</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.advancedCompliance.signature.appearance')}
          </label>
          <p className="text-gray-900">page/coords</p>
        </div>
      </div>

      {/* Status lines */}
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">
            {t('workflow.advancedCompliance.signature.signature')}:
          </span>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {t('workflow.advancedCompliance.signature.notApplied')}
          </span>
        </div>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">
            {t('workflow.advancedCompliance.signature.timestamp')}:
          </span>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {t('workflow.advancedCompliance.signature.notApplied')}
          </span>
        </div>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">
            {t('workflow.advancedCompliance.signature.target')}:
          </span>
          <span className="text-sm text-gray-600">report_v1_final.pdf</span>
        </div>
      </div>

      {/* Disabled buttons */}
      <div className="flex flex-wrap gap-3">
        <button 
          disabled 
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
        >
          <Eye className="h-4 w-4 mr-2" />
          {t('workflow.advancedCompliance.signature.testSignature')}
        </button>
        <button 
          disabled 
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
        >
          <Lock className="h-4 w-4 mr-2" />
          {t('workflow.advancedCompliance.signature.signFinalPDF')}
        </button>
        <button 
          disabled 
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          {t('workflow.advancedCompliance.signature.verify')}
        </button>
      </div>

      {/* Info message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExternalLink className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-blue-800 text-sm">
              {t('workflow.advancedCompliance.signature.infoMessage')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEarlyAccessSection = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('workflow.advancedCompliance.earlyAccess.title')}
        </h3>
        
        <form onSubmit={handleEarlyAccessSubmit} className="space-y-4">
          <div>
            <label htmlFor="useCase" className="block text-sm font-medium text-gray-700 mb-1">
              {t('workflow.advancedCompliance.earlyAccess.useCase')}
            </label>
            <textarea
              id="useCase"
              rows={4}
              value={earlyAccessData.useCase}
              onChange={(e) => handleEarlyAccessInputChange('useCase', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('workflow.advancedCompliance.earlyAccess.useCasePlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="regulatorMarket" className="block text-sm font-medium text-gray-700 mb-1">
              {t('workflow.advancedCompliance.earlyAccess.regulatorMarket')}
            </label>
            <input
              type="text"
              id="regulatorMarket"
              value={earlyAccessData.regulatorMarket}
              onChange={(e) => handleEarlyAccessInputChange('regulatorMarket', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('workflow.advancedCompliance.earlyAccess.regulatorMarketPlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-1">
              {t('workflow.advancedCompliance.earlyAccess.deadline')}
            </label>
            <input
              type="date"
              id="deadline"
              value={earlyAccessData.deadline}
              onChange={(e) => handleEarlyAccessInputChange('deadline', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-1">
              {t('workflow.advancedCompliance.earlyAccess.contactEmail')}
            </label>
            <input
              type="email"
              id="contactEmail"
              value={earlyAccessData.contactEmail}
              onChange={(e) => handleEarlyAccessInputChange('contactEmail', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('workflow.advancedCompliance.earlyAccess.contactEmailPlaceholder')}
            />
          </div>

          <button
            type="submit"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Mail className="h-5 w-5 mr-2" />
            {t('workflow.advancedCompliance.earlyAccess.requestButton')}
          </button>
        </form>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderXBRLTaggingSection();
      case 2:
        return renderDigitalSignatureSection();
      case 3:
        return renderEarlyAccessSection();
      default:
        return renderXBRLTaggingSection();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <WorkflowTabView
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepClick}
        previewOnly={previewOnly}
        onComplete={() => {
          // Handle workflow completion
          console.log('Advanced Compliance workflow completed');
        }}
      >
        {renderCurrentStep()}
      </WorkflowTabView>
    </div>
  );
};

export default AdvancedComplianceWorkflow;
